import * as _ from 'lodash-es';
import { Parchment } from 'quill/core';
import React from 'react';
import Comment from './Comment';
import Diff from './Diff';
import ReactBlockEmbed from './ReactBlockEmbed';
import Reaction from './Reaction';
import Span from './Span';
import VersionChange from './VersionChange';
import ButtonComponent from '../button/Button';

export type ButtonType = 'action' | 'navigation';

type State = {
  buttonText?: string;
  type: ButtonType;
  destination: string;
};

const BUTTON_TEXT_ATTRIBUTE = 'data-blot-button-text';

class ButtonBlot extends ReactBlockEmbed<State> {
  static blotName = 'button';
  static className = 'ql-button';
  static tagName = 'div';

  static formats(domNode: Element) {
    const attributes: {
      'button-text'?: string | null;
    } = {};

    if (domNode.hasAttribute(BUTTON_TEXT_ATTRIBUTE)) {
      attributes['button-text'] = domNode.getAttribute(BUTTON_TEXT_ATTRIBUTE);
    }
    return _.isEmpty(attributes) ? null : attributes;
  }

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: HTMLElement,
    value: Omit<State, 'buttonText'>,
  ) {
    const buttonText =
      ButtonBlot.formats(domNode)?.['button-text'] ?? undefined;

    super(scroll, domNode, { ...value, buttonText });

    this.setState(value);
  }

  format(name: string, value: unknown) {
    if (
      name === 'button-text' &&
      (typeof value === 'string' || value == null)
    ) {
      this.setButtonText(value ?? undefined);
    } else if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Span.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor) {
        // @ts-expect-error
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  setButtonText(buttonText: string | undefined) {
    this.setState({ buttonText });
    if (this.domNode instanceof HTMLElement) {
      if (buttonText == null) {
        this.domNode.removeAttribute(BUTTON_TEXT_ATTRIBUTE);
      } else {
        this.domNode.setAttribute(BUTTON_TEXT_ATTRIBUTE, buttonText);
      }
    }
  }

  render(state: State) {
    return (
      <ButtonComponent
        buttonText={state.buttonText}
        destination={state.destination}
        type={state.type}
        onButtonTextChange={(buttonText) => this.setButtonText(buttonText)}
      />
    );
  }

  getReferenceNodeForBounds() {
    return this.domNode.querySelector('.button-container') || this.domNode;
  }

  formats() {
    return { ...this.attributes.values(), ...ButtonBlot.formats(this.domNode) };
  }

  getState() {
    return this.store.getState();
  }
}

export default ButtonBlot;
